<template>
  <div class="flex flex-col h-full w-full">
    <LoadingView v-if="isLoading" />
    <header class="flex items-center justify-between gap-4 flex-shrink-0 w-full">
      <h1 class="flex-0 text-3xl leading-9 font-headline font-medium antialiased  bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text inline-block">Available Homes</h1>
    </header>
    <div class="flex flex-col mt-4 p-4 rounded-lg">
      <div class="inline-flex flex-col flex-wrap md:flex-row md:justify-start gap-6">
        <div class="mb-4 flex-initial">
          <label for="search" class="block text-sm font-medium leading-5 pb-1.5 text-blue-500">Search by Keyword</label>
          <SearchInput v-model="query.search" color="gray" placeholder="Search..." />
        </div>
      <div class="mb-4 flex-initial">
        <SelectMultipleField v-model="query.status" label="Published" :options="Statuses.statuses" all="All Statuses"/>
      </div>
      <div class="mb-4 flex-initial">
        <SelectField v-model="query.lot" label="Lot Status" :options="lot_statuses" empty="All Statuses" :allow-null="true" />
      </div>
      <div class="mb-4 flex-initial">
        <SelectField v-model="query.type" label="Type" :options="[{label: 'Market', value: 'Market'}, {label: 'Model', value: 'Model'}]" empty="All Types" :allow-null="true" />
      </div>
      <div v-if="states.length" class="mb-4 flex-initial">
        <SelectMultipleField v-model="query.state" label="States" :options="states.map(state => ({label: state, value: state}))" empty="All States" all="All States" />
      </div>
      <div v-if="communities.length" class="mb-4 flex-initial">
        <SelectMultipleField v-model="query.community" label="Community" :options="communities.map(community => ({ label: community.meta?.info?.name ?? community.kova?.WebGroupName, value: community }))" empty="All Communities" all="All Communities" />
      </div>
      <div v-if="collections.length > 1" class="mb-4 flex-initial">
        <SelectMultipleField v-model="query.collection" label="Collection" :options="collections.map(collection => ({label: collection.kova?.MarketingName, value: collection.community_rid}))" empty="All Collections" all="All Collections" />
      </div>
      <div v-if="availableFloorplans.length > 1" class="mb-4 flex-initial">
        <SelectMultipleField v-model="query.floorplan" label="Floorplan" :options="availableFloorplans.map(floorplan => ({label: floorplan, value: floorplan}))" empty="All Floorplans" all="All Floorplans" />
      </div>
      <div v-if="hasFilters" class="mb-4 flex-initial sm:mt-5">
        <FormButton @click="clear" class="bg-bhav-blue-500 text-white">Clear Filters</FormButton>
      </div>
      <div class="mb-4 flex-initial sm:mt-6 ml-auto">
        <button @click="showColumns = !showColumns" class="w-9 h-9 items-center justify-center bg-transparent hover:bg-bhav-cool-gray-50 text-bhav-blue-200 hover:text-bhav-blue-500 rounded-lg text-lg">
          <p class="relative">
            <span class="fak fa-col-options leading-none"></span>
            <span class="sr-only">Open Table Column Preferences Drawer</span>
          </p>
        </button>
      </div>
    </div>

    <PaginationTable empty-message="There are currently no available home items." v-if="!isLoading" :legacy="false"  :show-columns="showColumns" @close-drawer="showColumns = false" :data="filtered" :columns="columns" view="available-homes-list">

        <template #headers>
          
        </template>
        <template #col-address="{ row }">
          <router-link class="block whitespace-nowrap" :to="link(row)">
            <div class="flex items-center">
              <div class="flex-shrink-0 rounded-full overflow-hidden">
                <img v-if="row.image" class="h-10 w-10 aspect-square object-cover"
                  :src="row.image" alt="">
                <i v-else class="fa-duotone fa-floorplan fa-2xl"></i>
              </div>
              <div class="px-4">
                <div class="text-sm leading-5 font-medium text-bhav-blue-500">
                  {{ row.kova?.Lot?.StreetAddress }}, {{ row.kova?.Lot?.City }}, {{ row.kova?.Lot?.State }}
                  {{ row.kova?.Lot?.ZipCode }}
                  <span v-if="row.status == 'new'" class="bg-bhav-cool-gray-50 text-bhav-blue-500 px-2 rounded-full inline-block text-xs leading-5 ml-1">{{ Statuses.label('new') }}</span>
                </div>
                <!-- <div class="text-sm leading-5 text-oakwood-gray-500">
                  {{ row.kova?.Name }}
                </div> -->
              </div>
            </div>
          </router-link>
        </template>

        <template #col-floorplan="{ row }">
          <router-link class="block whitespace-nowrap" :to="link(row)">
            <div class="text-sm leading-5 text-bhav-blue-500">
              {{ row.floorplan_name }}
            </div>
          </router-link>
        </template>

        <template #col-collection="{ row }">
          <router-link class="block whitespace-nowrap" :to="link(row)">
            <div class="text-sm leading-5 text-bhav-blue-500">
              {{ row.collection_name }}
            </div>
          </router-link>
        </template>

        <template #col-price="{ row }">
          <router-link class="block whitespace-nowrap" :to="link(row)">
            <div class="text-sm leading-5 text-bhav-blue-500">
              {{ formatToCurrency(row.meta?.info?.price || row.kova?.Price) }}
            </div>
          </router-link>
        </template>

        <template #col-status="{ row }">
          <router-link class="block whitespace-nowrap" :to="link(row)">
            <span
              class="px-3 h-8 items-center justify-center inline-flex text-xs leading-5 font-semibold rounded-3xl"
              :class="[row.kova?.Lot?.Status === 'Available' ? 'bg-bhav-green-100 text-bhav-green-600' : 'bg-bhav-bright-blue-100 text-bhav-bright-blue-500']">
              {{ row.kova?.Lot?.Status }}
            </span>
          </router-link>
        </template>

        <template #col-type="{ row }">
          <router-link class="block whitespace-nowrap" :to="link(row)">
            <span
              class="px-3 h-8 items-center justify-center inline-flex text-xs leading-5 font-semibold rounded-3xl"
              :class="[row.kova?.Lot?.Type === 'Model' ? 'bg-oakwood-orange-200 text-oakwood-orange-800' : 'bg-bhav-green-100 text-bhav-green-600']">
              {{ row.kova?.Lot?.Type }}
            </span>
          </router-link>
        </template>

        <template #col-lot="{ row }">
          <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            {{ row.kova?.Lot.LotNumber ?? '-' }}
          </router-link>
        </template>

        <template #col-elevation="{ row }">
          <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            {{ row.kova?.HouseName ?? '-' }}
          </router-link>
        </template>

        <template #col-bedrooms="{ row }">
          <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            {{ row.meta.info.bedrooms || '-' }}
          </router-link>
        </template>

        <template #col-bathrooms="{ row }">
          <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            {{ formatToNumber(((row.meta.info.bathrooms?.full || 0) * 1) + ((row.meta.info.bathrooms?.half || 0) * .5)) }}
          </router-link>
        </template>

        <template #col-garages="{ row }">
          <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            {{ row.meta.info.garages || '-' }}
          </router-link>
        </template>
        
        <template #col-sqft="{ row }">
          <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            {{ formatToNumber(row.meta.info.sqft || '-') }}
          </router-link>
        </template>

        <template #col-latlng="{ row }">
          <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            <dl>
              <dt class="sr-only">Latitude:</dt>
              <dd>{{ formatToNumber(row.meta.info?.geo?.lat || 0) }}°</dd>
              <dt class="sr-only">Longitude:</dt>
              <dd>{{ formatToNumber(row.meta.info?.geo?.lng || 0) }}°</dd>
            </dl>
          </router-link>
        </template>

        <template #col-rate="{ row }">
          <router-link class="block py-4" :to="link(row)">
            {{ row.meta.info.rate || '-' }}
          </router-link>
        </template>

        <template #col-callout="{ row }">
          <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            <span
            class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-bhav-green-100 text-bhav-green-600">
              {{ !['',null,'N/S'].includes(row.meta.info.callout) ? row.meta.info.callout : '' }}
            </span>
          </router-link>
        </template>

        <template #col-notes="{ row }">
          <router-link class="block py-4" :to="link(row)">
            {{ row.meta.info.notes || '-' }}
          </router-link>
        </template>

        <template #col-published="{ row }">
          <div class="flex items-center gap-2">
            <ToggleButton v-if="GlobalPolicy.edit()" :id="`item-${row.id}-status`" @changed="save(row)" :checked="row.status == 'active'"
              on-color="green"
              :label="Statuses.label(row.status)"/>
            <template v-else>
              <p title="Published" class="text-bhav-blue fas fa-square-check text-xl" v-if="row.status == 'active'"></p>
              <p title="Unpublished" class="text-oakwood-gray-400 fas fa-square-x text-xl" v-else></p>
            </template>
          </div>
        </template>

        <template #col-featured-home="{ row }">
          <router-link class="block py-4" :to="link(row)">
            <span
                class="fak text-3xl text-bhav-blue-500"
                :class="row.meta?.featured === true ? 'fa-star' : 'fa-star-outlined'">
            </span>
          </router-link>
        </template>

    </PaginationTable>
  </div>
  </div>

</template>

<script>
import axios from "axios";
import { formatToCurrency, formatToNumber } from '@/helpers/numberHelpers';
import LoadingView from '@/components/LoadingView.vue';
import {ToggleButton,SelectField,SelectMultipleField,FormButton,SearchInput} from '@/components/form';
import { mapState, mapWritableState, mapActions } from 'pinia';
import { useMarketHomeStore } from "@/stores/market_homes";
import PaginationTable from "@/components/PaginationTable.vue";
import { GlobalPolicy } from "@/policies/global";
import { useMessageStore } from "@/stores/message";
import Statuses from "@/enums/Statuses";

export default {
  setup() {
      return {
          GlobalPolicy,
          Statuses,
          formatToCurrency,
          formatToNumber,
          link: (row) => ({ name: 'available-homes-show', params: { id: row.id } }),
          columns: [
            {
              name: 'address',
              label: 'Address',
              sort: (row) => `${row.kova?.Lot?.StreetAddress}, ${row.kova?.Lot?.City}, ${row.kova?.Lot?.State} ${row.kova?.Lot?.ZipCode}`,
              default: true
            },
            {
              name: 'floorplan',
              label: 'Floorplan',
              sort: (row) => row.floorplan_name,
              default: true
            },
            {
              name: 'collection',
              label: 'Collection',
              sort: (row) => row.collection_name,
              default: true
            },
            {
              name: 'price',
              label: 'Price',
              sort: (row) => row.meta?.info?.price || row.kova?.Price || 0,
              default: true
            },
            {
              name: 'status',
              label: 'Lot Status',
              sort: (row) => row.kova?.Lot?.Status || '',
              default: true
            },
            {
              name: 'type',
              label: 'Type',
              sort: (row) => row.kova?.Lot?.Type || '',
              default: false
            },
            {
              name: 'lot',
              label: 'Lot',
              sort: (row) => row.kova?.Lot.LotNumber || 0,
              default: false
            },
            {
              name: 'elevation',
              label: 'Elevation',
              sort: (row) => row.kova?.HouseName || '',
              default: false
            },
            {
              name: 'bedrooms',
              label: 'Bedrooms',
              sort: (row) => row.meta.info.bedrooms ?? 0,
              default: false
            },
            {
              name: 'bathrooms',
              label: 'Bathrooms',
              sort: (row) => (row.meta.info.bathrooms.full ?? 0) + ((row.meta.info.bathrooms.half ?? 0) * .5),
              default: false
            },
            {
              name: 'garages',
              label: 'Garages',
              sort: (row) => row.meta.info.garages ?? 0,
              default: false
            },
            {
              name: 'sqft',
              label: 'Square Footage',
              sort: (row) => row.meta.info.sqft ?? 0,
              default: false
            },
            {
              name: 'latlng',
              label: 'Lat/Lng',
              sort: (row) => (row.meta.info?.geo?.lat || 0),
              default: false
            },
            {
              name: 'rate',
              label: 'Advertised Rate',
              sort: (row) => row.meta.info.rate ?? '',
              default: false
            },
            {
              name: 'callout',
              label: 'Callout',
              sort: (row) => row.meta.info.callout ?? 'N/S',
              default: false
            },
            {
              name: 'notes',
              label: 'Internal Notes',
              sort: (row) => row.meta.info.notes ?? '',
              default: false
            },
            {
              name: 'published',
              label: 'Published',
              sort: (row) => Statuses.label(row.status),
              default: true
            },
            {
              name: 'featured-home',
              label: 'Featured Home',
              sort: (row) => row.meta.info.notes ?? '',
              default: false
            },
          ]
      }
  },
  data() {
    return {
      salesOrders: [],
      communities: [],
      floorplans: [],
      isLoading: true,
      showColumns: false,
      lot_statuses: [
        {
          label : 'Available',
          value : 'Available'
        },
        {
          label : 'New',
          value : 'New'
        }
      ]
    }
  },
  methods: {
    ...mapActions(useMarketHomeStore, ['clear']),
    ...mapActions(useMessageStore, ['setAlert']),
    index() {
      axios.get(`sales_orders`)
        .then(res => {
          this.salesOrders = res.data;
          this.isLoading = false;
          this.indexCommunities();
          this.indexFloorplans();
        })
        .catch(e => {
          throw e;
        });
    },
    indexCommunities() {
      axios
      .get(`divisions/communities`)
      .then((res) => {
        let collection_rids = new Set(this.salesOrders.map(salesOrder => salesOrder.kova.CommunityRID));
        this.communities = res.data
          .map(community => {
            community.collections = community.collections
              .filter(collection => collection.status != 'inactive' && collection_rids.has(collection.community_rid))
              .sort((a,b) => a.meta.info.name > b.meta.info.name ? 1 : -1);
            return community;
          })
          .filter(community => community.status != 'inactive' && community.collections.length > 0)
          .sort((a,b) => (a.meta?.info?.name || a.kova?.WebGroupName) > (b.meta?.info?.name || b.kova?.WebGroupName) ? 1 : -1);
        this.isLoading = false;
      })
      .catch((e) => {
        throw e;
      });
    },
    indexFloorplans() {
      axios
      .get(`floorplans`)
      .then((res) => {
        let available = [...new Set(this.salesOrders.map(home => home.kova.ModelRID))];
        this.floorplans = res.data
          .filter(floorplan =>  
          available.includes(floorplan.kova.ModelRID));
      })
      .catch((e) => {
        throw e;
      });
    },
    save(item) {
      const status = item.status;
      item.status = item.status != 'active' ? 'active' : 'inactive';
      axios.patch(`sales_orders/${item.id}`, item)
      .then(res => {
        if(res.data?.id) {
          this.index();
        }
      })
      .catch(e => {
        item.status = status;
        throw e;
      });
    },
  },
  computed: {
    ...mapState(useMarketHomeStore, ['hasFilters']),
    ...mapWritableState(useMarketHomeStore, ['query', 'page']),
    filtered() {
      let results = this.salesOrders;
      if (!this.query.status.includes(null)) {
        results = results.filter(item => this.query.status.includes(item.status));
      }

      if (this.query.type !== null) {
        results = results.filter(item => this.query.type === item.kova?.Lot?.Type);
      }

      if (this.query.state.length > 0) {
        results = results.filter(item => this.query.state.includes(item.kova.Lot.State));
      }

      if (this.query.floorplan.length > 0) {
        results = results.filter(item => this.query.floorplan.includes(item.floorplan_name));
      }

      if (this.query.collection.length > 0) {
        results = results.filter(item => this.query.collection.includes(item.kova.CommunityRID));
      }

      if (this.query.community.length > 0) {
        let collections = this.query.community.flatMap(community => community.collections.map(collection => collection.community_rid));
        results = results.filter(item => collections.includes(item.kova.CommunityRID))
      }

      if (this.query.lot !== null) {
        results = results.filter(item => this.query.lot === item.kova.Lot?.Status);
      }

      if (this.query.search != '') {
        const searchTerm = this.query.search.toLowerCase();
        results = results.filter(item => {
          const address = (`${item.kova?.Lot?.StreetAddress || ''}, ${item.kova?.Lot?.City || ''}, ${item.kova?.Lot?.State || ''} ${item.kova?.Lot?.ZipCode || ''}`).toLowerCase();
          const price = (formatToCurrency(item.meta?.info?.price || item.kova?.Price || '')).toString().toLowerCase();
          const lot = (item.kova?.Lot?.LotNumber || '').toString().toLowerCase();
          const elevation = (item.kova?.HouseName || '').toLowerCase();
          const bathrooms = (formatToNumber(((item.meta.info.bathrooms?.full || 0) * 1) + ((item.meta.info.bathrooms?.half || 0) * .5))).toString().toLowerCase();
          const bedrooms = (item.meta?.info?.bedrooms || '').toString().toLowerCase();
          const garages = (item.meta?.info?.garages || '').toString().toLowerCase();
          const sqft = (formatToNumber(item.meta?.info?.sqft || '')).toString().toLowerCase();
          const lat = (formatToNumber(item.meta.info?.geo?.lat || 0)).toString().toLowerCase();
          const lng = (formatToNumber(item.meta.info?.geo?.lng || 0)).toString().toLowerCase();
          const rate = (item.meta?.info?.rate || '').toLowerCase();
          const callout = (item.meta?.info?.callout || '').toLowerCase();
          const notes = (item.meta?.info?.notes || '').toLowerCase();

          return address.includes(searchTerm) ||
                 price.includes(searchTerm) ||
                 lot.includes(searchTerm) ||
                 elevation.includes(searchTerm) ||
                 bathrooms.includes(searchTerm) ||
                 bedrooms.includes(searchTerm) ||
                 garages.includes(searchTerm) ||
                 sqft.includes(searchTerm) ||
                 lat.includes(searchTerm) ||
                 lng.includes(searchTerm) ||
                 rate.includes(searchTerm) ||
                 callout.includes(searchTerm) ||
                 notes.includes(searchTerm);
        });
      } 
      return results;
    },
    states() {
      if(this.salesOrders.length == 0) return [];
      return [...new Set(this.salesOrders.map((home) => home.kova.Lot.State))];
    },
    collections() {
      if(this.communities.length == 0 || this.query.community.length == 0) return [];
      return this.query.community.flatMap(community => community.collections).sort((a, b) => (a.kova?.MarketingName) > (b.kova?.MarketingName) ? 1 : -1);
    },
    availableFloorplans() {
      let floorplans = this.floorplans;
      if(this.query.collection.length === 0) return [];
      if(this.floorplans.length == 0) return [];
      if(this.query.collection.length > 0) {
        floorplans = floorplans.filter(floorplan => this.query.collection.includes(floorplan.kova.CommunityRID));
      } else if(this.query.community.length) {
        let community_rids = this.collections.map(collection => collection.community_rid);
        floorplans = floorplans.filter(floorplan => community_rids.includes(floorplan.kova.CommunityRID))
      }
      return [...new Set(floorplans.map(floorplan => floorplan.meta?.info?.name))];
    }
  },
  watch: {
    "query.community": {
      handler(newVal, oldVal) {
        if(newVal.length !== oldVal.length && this.query.collection.length > 0) {
          let collections = newVal.flatMap(community => community.collections.map(collection => collection.community_rid));
          this.query.collection = this.query.collection.filter(collection => collections.includes(collection));
        }
      },
      deep: true
    },
    "query.collection": {
      handler(newVal, oldVal) {
        if(newVal.length !== oldVal.length && this.query.floorplan.length > 0) {
          if(newVal.length === 0) {
            this.query.floorplan = [];
          }
        }
      }
    },
    query: {
      handler() { this.page = 1; },
      deep: true
    }
    },
  created() {
    this.index();
  },
  components: {
    LoadingView,
    ToggleButton,
    SelectField,
    SelectMultipleField,
    FormButton,
    PaginationTable,
    SearchInput
  },
}
</script>